import React from "react";
import { Button, LinearProgress } from "@mui/material";
import { useTranslation } from "react-i18next";

/**
 * The one way a report says "new numbers are coming".
 *
 * A report refetches the moment a filter changes, and the old numbers used to sit there
 * looking final until the new ones landed - nobody could tell a slow server from a dead
 * one. Wrap the result area (KPI strip, table) in this:
 *
 *   nothing yet, loading     the page's own skeleton, so the layout holds its shape
 *   numbers showing, loading a thin bar + "Updating..." pinned over the area, the old
 *                            numbers faded and click-paused so a stale row can't be
 *                            drilled into or printed as current
 *   failed                   a red line with Try again - never the "no data" text,
 *                            which would read as an empty period. A failed refetch
 *                            keeps the last good numbers under the line.
 *   otherwise                the children, which own their empty-state text
 *
 * className sizes the region; bodyClassName the box holding the children - pass
 * "flex-1 min-h-0 flex flex-col" to both when the children own a scroll area that must
 * fill a flex column.
 *
 * The bar takes no height (it floats in a zero-height sticky strip), so nothing below
 * it jumps when a refetch starts or ends.
 */
const RefreshRegion = ({
  loading = false,
  hasData = false,
  error = false,
  onRetry,
  skeleton = null,
  className = "",
  bodyClassName = "",
  children,
}) => {
  const { t } = useTranslation();

  if (!hasData) {
    if (loading) return skeleton;
    if (error) return <LoadError onRetry={onRetry} className="rounded-[14px] bg-white" />;
    return children;
  }

  return (
    <div className={`relative ${className}`} aria-busy={loading || undefined}>
      {loading && (
        <div className="sticky top-0 z-[3] h-0">
          <LinearProgress
            aria-label={t("reportUpdating")}
            sx={{ position: "absolute", insetInline: 0, top: 0, height: "3px", borderRadius: "2px" }}
          />
          <span
            role="status"
            aria-live="polite"
            className="absolute end-2 top-[6px] rounded-full bg-white/95 px-2 py-[1px] text-[11px] font-medium text-gray-500 shadow-sm"
          >
            {t("reportUpdating")}
          </span>
        </div>
      )}
      {error && !loading && <LoadError onRetry={onRetry} className="mb-2 rounded-[14px] bg-white" />}
      <div className={`transition-opacity duration-150 ${bodyClassName} ${loading ? "pointer-events-none select-none opacity-50" : ""}`}>
        {children}
      </div>
    </div>
  );
};

/** The red "couldn't load" line with Try again - shared with DynamicTable. */
export const LoadError = ({ onRetry, className = "" }) => {
  const { t } = useTranslation();
  return (
    <div role="alert" className={`flex flex-wrap items-center justify-center gap-2 px-3 py-3 text-[13px] ${className}`}>
      <span className="font-medium text-red-500">{t("reportLoadFailed")}</span>
      {onRetry && (
        <Button size="small" variant="outlined" color="error" onClick={onRetry} sx={{ py: 0, textTransform: "none" }}>
          {t("retryLoad")}
        </Button>
      )}
    </div>
  );
};

export default RefreshRegion;
